import { Component } from '@theme/component';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/** The clock starts again from this many seconds once it has run out. */
const SECONDS_AGAIN = 90;

/**
 * The moment the clock runs out. It is kept here and not on the element: the drawer is drawn
 * again when the cart changes and the band leaves the page while the cart is empty, and the
 * clock goes on through both. A new page starts it again.
 */
let endsAt = 0;

/**
 * @typedef {object} CartTimerRefs
 * @property {HTMLElement} clock - The element that shows the time left.
 */

/**
 * The band in the cart drawer that says how long the cart is reserved for
 * (snippets/ell-cart-timer.liquid). The clock starts the first time the drawer is opened.
 *
 * @extends {Component<CartTimerRefs>}
 */
class EllCartTimer extends Component {
  requiredRefs = ['clock'];

  /** @type {number | undefined} */
  #interval;

  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();

    const drawer = this.closest('theme-drawer');
    drawer?.addEventListener(DrawerOpenEvent.eventName, this.#start, { signal: this.#abort.signal });

    if (endsAt || drawer?.hasAttribute('open')) this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  #start = () => {
    if (!endsAt) endsAt = Date.now() + this.#seconds * 1000;

    this.#draw();
    if (!this.#interval) this.#interval = setInterval(this.#draw, 1000);
  };

  #draw = () => {
    let left = Math.round((endsAt - Date.now()) / 1000);

    if (left <= 0) {
      left = SECONDS_AGAIN;
      endsAt = Date.now() + left * 1000;
    }

    const minutes = String(Math.floor(left / 60)).padStart(2, '0');
    const seconds = String(left % 60).padStart(2, '0');
    this.refs.clock.textContent = `${minutes}:${seconds}`;
  };

  /** @returns {number} */
  get #seconds() {
    return Number.parseInt(this.dataset.seconds ?? '', 10) || 180;
  }
}

if (!customElements.get('ell-cart-timer-component')) {
  customElements.define('ell-cart-timer-component', EllCartTimer);
}
